import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  ARTIFACT_TYPES,
  CONTEXT_POLICIES,
  createRuleSchema,
  EFFECT_TYPES,
  EVENT_TYPES,
  EXECUTOR_TYPES,
  RULE_STATUS,
  TRIGGER_EVENT_TYPES,
  type CreateRuleInput,
} from "@aos/shared";
import { Separator } from "@/components/ui/separator";
import { FormShell } from "../skills/_components/FormShell";
import {
  NumberField,
  optionsFromEnum,
  SelectField,
  TextField,
} from "../skills/_components/form";

export interface RuleFormProps {
  action: "create" | "edit";
}

/**
 * Create/edit form for a Rule. Mirrors the rule DSL (§7 / IMPLEMENTATION_CONTRACT
 * §14): identity + trigger/guard conditions + effect. Validated against
 * `createRuleSchema`.
 */
export function RuleForm({ action }: RuleFormProps) {
  const form = useForm<CreateRuleInput>({
    resolver: zodResolver(createRuleSchema),
    refineCoreProps: { resource: "rules", action },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
  } = form;

  return (
    <FormShell
      title={action === "create" ? "New Rule" : "Edit Rule"}
      resource="rules"
      isSubmitting={formLoading}
      onSubmit={handleSubmit((values) => onFinish(values))}
    >
      <TextField form={form} name="name" label="Name" placeholder="Rule name" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="status"
          label="Status"
          options={optionsFromEnum(RULE_STATUS)}
        />
        <NumberField
          form={form}
          name="priority"
          label="Priority"
          placeholder="100"
        />
      </div>
      <SelectField
        form={form}
        name="context_policy"
        label="Context Policy"
        options={optionsFromEnum(CONTEXT_POLICIES)}
        allowEmpty
      />

      <Separator />
      <p className="text-sm font-medium">Trigger &amp; conditions</p>
      <SelectField
        form={form}
        name="trigger_event_type"
        label="Trigger Event Type"
        options={optionsFromEnum(TRIGGER_EVENT_TYPES)}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="artifact_type"
          label="Artifact Type"
          options={optionsFromEnum(ARTIFACT_TYPES)}
          allowEmpty
        />
        <TextField
          form={form}
          name="required_status"
          label="Required Status"
          placeholder="e.g. new"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="required_phase_id"
          label="Required Phase ID"
          placeholder="phs_…"
        />
        <TextField
          form={form}
          name="required_skill_id"
          label="Required Skill ID"
          placeholder="e.g. simple-task-agent"
        />
      </div>
      <TextField
        form={form}
        name="required_evidence_type"
        label="Required Evidence Type"
        placeholder="e.g. pricing_signal"
      />
      <NumberField
        form={form}
        name="required_evidence_count"
        label="Required Evidence Count"
        placeholder="e.g. 3"
      />

      <Separator />
      <p className="text-sm font-medium">Effect</p>
      <SelectField
        form={form}
        name="effect_type"
        label="Effect Type"
        options={optionsFromEnum(EFFECT_TYPES)}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="target_status"
          label="Target Status"
          placeholder="e.g. done"
        />
        <TextField
          form={form}
          name="target_phase_id"
          label="Target Phase ID"
          placeholder="phs_…"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="target_skill_id"
          label="Target Skill ID"
          placeholder="simple-task-agent"
        />
        <SelectField
          form={form}
          name="executor_type"
          label="Executor Type"
          options={optionsFromEnum(EXECUTOR_TYPES)}
          allowEmpty
        />
      </div>
      <SelectField
        form={form}
        name="emitted_event_type"
        label="Emitted Event Type"
        options={optionsFromEnum(EVENT_TYPES)}
        allowEmpty
      />
    </FormShell>
  );
}

export default RuleForm;
